/**
 * One photo from a connected source (Google Photos, a home NAS, …). Kinmantle
 * never stores photos: the bytes stay with the source and only these
 * references travel to the wall.
 */
export interface Photo {
  /** Unique across sources, e.g. "gphotos:<id>" or "nas:<path hash>". */
  id: string;
  album: string | null;
  caption: string | null;
  /**
   * When the photo was taken, as local wall-clock time with no zone
   * ("YYYY-MM-DDTHH:MM:SS"), the way cameras record it. Null if unknown.
   */
  takenAt: string | null;
  width: number;
  height: number;
  /** Image for full-screen and large frames. */
  url: string;
  /** Smaller image for cards and blurred backdrops (may equal `url`). */
  thumbUrl: string;
  /** A short description for screen readers (the photo agent's, for wall photos). */
  alt?: string | null;
  tags?: string[];
  /** Where the subject is, for framing (frame.ts); absent when unknown. */
  focus?: { x: number; y: number } | null;
  subject?: { core: number[]; loose: number[]; faces?: number } | null;
}

export type PhotoDayReason = "onThisDay" | "dailyPick";

export interface PhotoOfDay {
  date: string;
  photo: Photo | null;
  reason: PhotoDayReason | null;
  yearsAgo: number | null;
}

/** "June 14, 2019" from a takenAt value (no timezone conversion: it is already local). */
export function formatTakenDate(takenAt: string | null): string | null {
  if (!takenAt) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(takenAt);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
}

/** A wall photo's id: the agent's 24 hex digits, or a demo drawing's name. */
export const PHOTO_ID = /^[a-z0-9-]{1,64}$/;

/**
 * Likes and flags from the household's screens. A flagged photo is never shown again,
 * like a photo kept private (docs/MEDIA.md, "Likes and flags").
 */
export interface PhotoMarks {
  liked: string[];
  flagged: string[];
}
